import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

const Contact = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const floatingAnimation = {
    y: [0, -15, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  return (
    <>
      <SEO 
        title="Contact Us - Blossom Cleaning Services | Get a Free Quote Today"
        description="Contact Blossom Cleaning Services in Kampala. Call 0752 058 190 or 0200 940 830 for a free quote. Located in Kololo opposite Libya Embassy. Available Mon-Sat 8AM-6PM."
        keywords="contact blossom cleaning, cleaning services quote Kampala, book cleaning service Uganda, Kololo cleaning services, cleaning company phone number"
        url="https://blossomcleaningservices.com/contact"
      />
      <Header />

      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated Background Elements */}
        <motion.div
          style={{
            position: "absolute",
            top: "15%",
            left: "8%",
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
          animate={floatingAnimation}
        />
        <motion.div
          style={{
            position: "absolute",
            bottom: "25%",
            right: "10%",
            width: 80,
            height: 80,
            borderRadius: "30%",
            background: "rgba(255,255,255,0.08)",
            rotate: 45,
          }}
          animate={{
            ...floatingAnimation,
            transition: { ...floatingAnimation.transition, delay: 2 },
          }}
        />

        <Container>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h1"
              sx={{
                mb: 3,
                color: "white",
                fontSize: { xs: "2.75rem", md: "4rem" },
                fontWeight: 800,
                background: "linear-gradient(45deg, #FFFFFF 30%, #E3F2FD 90%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Get In Touch
            </Typography>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <Typography
                variant="h5"
                sx={{
                  maxWidth: 700,
                  mx: "auto",
                  color: "rgba(255,255,255,0.95)",
                  fontSize: { xs: "1.2rem", md: "1.5rem" },
                  fontWeight: 400,
                  lineHeight: 1.6,
                }}
              >
                Ready to transform your space? Contact us today for a free
                consultation and quote.
              </Typography>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          backgroundColor: "background.default",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.03) 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.03) 0%, transparent 50%)`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="flex-start">
            {/* Contact Form */}
            <Grid item xs={12} md={8}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8 }}
              >
                <ContactForm title="Request a Free Quote" />
              </motion.div>
            </Grid>

            {/* Quick Contact Info */}
            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Box
                  sx={{
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)",
                    borderRadius: 4,
                    p: 4,
                    boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
                    border: "1px solid rgba(0,0,0,0.08)",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 4,
                      fontWeight: 700,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    Quick Contact
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    {/* Phone */}
                    <motion.div
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #4CAF50, #45a049)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <PhoneIcon sx={{ color: "white", fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            Call Us
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ color: "text.primary", fontWeight: 700 }}
                          >
                            0752 058 190
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ color: "text.primary", fontWeight: 700 }}
                          >
                            0200 940 830
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>

                    {/* WhatsApp */}
                    <motion.div
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #25D366, #128C7E)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <WhatsAppIcon sx={{ color: "white", fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            WhatsApp
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ color: "text.primary", fontWeight: 700 }}
                          >
                            Chat Instantly
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>

                    {/* Email */}
                    <motion.div
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #2196F3, #1976D2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <EmailIcon sx={{ color: "white", fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            Email Us
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              color: "text.primary",
                              fontWeight: 700,
                              fontSize: "1rem",
                            }}
                          >
                            bcitycleaningservices@gmail.com
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>

                    {/* Location */}
                    <motion.div
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #FF6B35, #F7931E)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <LocationOnIcon
                            sx={{ color: "white", fontSize: 24 }}
                          />
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            Visit Us
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              color: "text.primary",
                              fontWeight: 700,
                              fontSize: "1rem",
                            }}
                          >
                            Kololo — Opposite Libya Embassy
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", mt: 0.5 }}
                          >
                            Kampala, Uganda
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact Info Cards */}
      {/* <Box
        sx={{
          py: { xs: 6, md: 10 },
          backgroundColor: "background.paper",
          position: "relative",
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                mb: 2,
                fontWeight: 800,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                fontSize: { xs: "2.5rem", md: "3rem" },
              }}
            >
              How Can We Help?
            </Typography>
            <Typography
              variant="h6"
              align="center"
              color="text.secondary"
              sx={{
                mb: 6,
                maxWidth: 700,
                mx: "auto",
                fontSize: { xs: "1.1rem", md: "1.25rem" },
                lineHeight: 1.6,
              }}
            >
              Multiple ways to reach us and get the professional cleaning
              service you deserve
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {[
              {
                icon: <PhoneIcon sx={{ fontSize: 48 }} />,
                title: "Phone Support",
                content: "0752 058 190\n0200 940 830",
                action: "Available 24/7 for emergencies",
                gradient: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
                animation: "pulse",
              },
              {
                icon: <WhatsAppIcon sx={{ fontSize: 48 }} />,
                title: "WhatsApp Chat",
                content: "Instant messaging\nQuick responses",
                action: "Chat with us directly",
                gradient: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                animation: "bounce",
              },
              {
                icon: <EmailIcon sx={{ fontSize: 48 }} />,
                title: "Email Us",
                content: "contact@blossomcleaning.com\nFast email responses",
                action: "We reply within 2 hours",
                gradient: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
                animation: "float",
              },
              {
                icon: <LocationOnIcon sx={{ fontSize: 48 }} />,
                title: "Visit Office",
                content: "Kololo — Opposite Libya Embassy\nKampala, Uganda",
                action: "Mon-Sat: 7AM-7PM",
                gradient: "linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)",
                animation: "pulse",
              },
              {
                icon: <AccessTimeIcon sx={{ fontSize: 48 }} />,
                title: "Service Hours",
                content:
                  "Monday - Saturday: 7:00 AM - 7:00 PM\nSunday: 8:00 AM - 5:00 PM",
                action: "Extended hours available",
                gradient: "linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)",
                animation: "bounce",
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{
                    y: -10,
                    transition: { duration: 0.3 },
                  }}
                >
                  <Card
                    sx={{
                      height: "100%",
                      textAlign: "center",
                      p: 4,
                      borderRadius: 4,
                      background:
                        "linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)",
                      border: "1px solid rgba(0,0,0,0.05)",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: item.gradient,
                      },
                      "&:hover": {
                        boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
                      },
                      transition: "all 0.4s ease",
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      <motion.div
                        animate={
                          item.animation === "pulse"
                            ? {
                                scale: [1, 1.1, 1],
                                transition: { duration: 2, repeat: Infinity },
                              }
                            : item.animation === "bounce"
                            ? {
                                y: [0, -10, 0],
                                transition: { duration: 2, repeat: Infinity },
                              }
                            : item.animation === "float"
                            ? floatingAnimation
                            : {}
                        }
                        style={{ marginBottom: "24px" }}
                      >
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            background: item.gradient,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto",
                            color: "white",
                          }}
                        >
                          {item.icon}
                        </Box>
                      </motion.div>
                      <Typography
                        variant="h5"
                        sx={{
                          mb: 2,
                          fontWeight: 700,
                          color: "text.primary",
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 2,
                          whiteSpace: "pre-line",
                          lineHeight: 1.7,
                          color: "text.secondary",
                          fontWeight: 500,
                        }}
                      >
                        {item.content}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "primary.main",
                          fontWeight: 600,
                          fontSize: "0.9rem",
                        }}
                      >
                        {item.action}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

      {/* Map Section */}
      <Box sx={{ height: 500, width: "100%", position: "relative" }}>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ height: "100%", width: "100%" }}
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3989.7564!2d32.5825!3d0.3163!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMMKwMTgnNTguNyJOIDMywrAzNCc1Ny4wIkU!5e0!3m2!1sen!2sug!4v1234567890"
            width="100%"
            height="100%"
            style={{ border: 0, filter: "grayscale(0.2) contrast(1.1)" }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Blossom Cleaning Services Location"
          />
        </motion.div>
      </Box>

      <Footer />
    </>
  );
};

export default Contact;
